// sekcie_v1 (20. 10. 2026, 0.J): DetailRow je jeden, v components/Ui.jsx
// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 1 miest
// ╔══════════════════════════════════════════════════════════════════╗
// ║  _ModuleTemplate.jsx — REFERENČNÁ ŠABLÓNA MODULU MATRIX v2        ║
// ║  Ústava 9.D.1 bod 2 — anti-chyba systém.                          ║
// ╠══════════════════════════════════════════════════════════════════╣
// ║  NOVÝ MODUL VZNIKÁ KÓPIOU TOHTO SÚBORU — NIKDY NIE OD NULY.       ║
// ║  Tým sa ústavné systémové veci NEDAJÚ vynechať.                   ║
// ║                                                                  ║
// ║  POSTUP (ústava 9.D.1):                                          ║
// ║   1. PRE-FLIGHT dokument — schváli Mirko PRED prvým riadkom.      ║
// ║   2. cp _ModuleTemplate.jsx → views/MojModul.jsx                 ║
// ║   3. Vyplň CFG · EDIT_FIELDS · STATUS_FILTERS · polia formulára.  ║
// ║   4. Nahraď TODO značky. Skontroluj DoD checklist (dole).         ║
// ╠══════════════════════════════════════════════════════════════════╣
// ║  ČO JE V ŠABLÓNE ZABUDOVANÉ (ústavné systémové veci):            ║
// ║   • master-detail layout ......................... sek. 56       ║
// ║   • LOCK multi-user (cez useRecordEditor) ........ sek. 53.7      ║
// ║   • DIRTY dialóg pri odchode ..................... sek. 53.1      ║
// ║   • Form → Confirm + DIFF pred uložením .......... 12.0/3 · 53.8  ║
// ║   • audit kto/čo/kedy (HistoryPanel + auto-log) .. 12.0/1         ║
// ║   • fulltext hľadanie v zozname .................. sek. 56        ║
// ║   • SearchableSelect (žiadny plain <select>) ..... sek. 56.11     ║
// ║   • TINT pozadia (žiadne inline farby) ........... sek. 21.15     ║
// ║   • dedup kontrola (409 z backendu) .............. sek. 13        ║
// ║   • klávesnica (mod+N, /) + cheatsheet ........... sek. 54        ║
// ║   • breadcrumb — rieši Layout .................... sek. 53.2      ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { COLORS, TYPO, SPACE, RADIUS, TINT, SK, todayLocal, hladaj } from '../theme.js'
import {
  PageHeader, MasterDetail, SearchInput, ListRow, ListStatusBar,
  FilterChips, KpiRow, KpiCard, Tabs, EmptyPane, SearchableSelect, DetailRow } from '../components/Ui.jsx'
import { Modal } from '../components/Modal.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import { HistoryPanel } from '../components/HistoryPanel.jsx'
import { useToast } from '../components/Toast.jsx'
import SmsButton from '../components/SmsButton.jsx'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { useRecordEditor } from '../hooks/useRecordEditor.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { usePositionMemory } from '../hooks/usePositionMemory.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { StavVazby, StitokVazby, useStavVazieb } from '../components/ParovanieMKsoft.jsx'   // MKSOFT_VAZBY_V1
import { useSkupinaColors } from '../utils/skupinaColors.js'
import RowMenu from '../components/RowMenu.jsx'
import ExportMenu from '../components/ExportMenu.jsx'
import { useNavigate } from 'react-router-dom'
import { useConfirm } from '../components/ConfirmDialog.jsx'

// ╔═══ KONFIGURÁCIA MODULU — VYPLŇ PRI KOPÍROVANÍ ═══════════════════╗
// DÔLEŽITÉ: resource MUSÍ byť presne cesta API segmentu (napr. 'work-schedule'
// pre /api/work-schedule). Server zapisuje audit pod týmto názvom — ak sa
// nezhoduje, HistoryPanel nenájde históriu (ústava-review 9.D.3).
const CFG = {
  resource:    'parties',
  apiBase:     '/api/parties',
  titlePlural: 'Partie',
  newLabel:    'Nová partia',
}

// Polia pre DIFF (ústava 53.8) — useRecordEditor ich porovná initial vs. zmenené.
// label = ako sa pole volá v Confirm prehľade. format = voliteľné zobrazenie.
const EDIT_FIELDS = [
  { name: 'prefix', label: 'Druh číslovania' },
  { name: 'name',  label: 'Názov partie' },
  { name: 'color', label: 'Farba' },
  { name: 'notes', label: 'Poznámka' },
]

// Filter chips (ústava 21.15 — farby gest). key 'all' = bez filtra.
const STATUS_FILTERS = [
  { key: 'all',      label: 'Všetko',    color: COLORS.muted },
  { key: 'active',   label: 'Aktívne',   color: COLORS.add },
  { key: 'inactive', label: 'Neaktívne', color: COLORS.muted },
]
const STATUS_LABEL = { active: 'Aktívna', inactive: 'Neaktívna' }

// ══════════════════════════════════════════════════════════════════
// HLAVNÝ KOMPONENT — master-detail stránka (ústava sek. 56)
// ══════════════════════════════════════════════════════════════════

// skupinaFromPartieName — heuristika: z mena partia odvodí ID skupiny (zhoda s schedule_groups)
// Sadra → omietkár-sadra · Cement → omietkár-vc · Potery anhydrit → poterác-anhydrit
function skupinaFromPartieName(name) {
  const n = String(name || '').toLowerCase()
  if (n.includes('sadra')) return 'omietkár-sadra'
  if (n.includes('potery') && n.includes('anhydrit')) return 'poterác-anhydrit'
  if (n.includes('potery') && n.includes('cement')) return 'poterác-cement'
  if (n.includes('anhydrit')) return 'poterác-anhydrit'
  if (n.includes('cement')) return 'omietkár-vc'
  if (n.includes('vápen') || n.includes('vapen') || n.includes('vc')) return 'omietkár-vc'
  if (n.includes('maliar')) return 'maliar'
  if (n.includes('hodinov')) return 'hodinovkár'
  if (n.includes('majster') || n.includes('technik')) return 'majster'
  if (n.includes('šofér') || n.includes('sofer')) return 'šofer'
  if (n.includes('office') || n.includes('predaj')) return 'office'
  return 'bez-skupiny'
}

export default function ModuleTemplate() {
  const { user } = useAuth()                 // user pre LOCK (53.7)
  const toast = useToast()
  const confirm = useConfirm()
  const skupinaColor = useSkupinaColors()    // farby skupín — zhoda s Rozpis prác (DB schedule_groups)

  // ── pamäť pozície (ústava 53.3) — obnoví výber / filter / hľadanie / scroll ──
  const pos = usePositionMemory(CFG.resource)
  const listRef = useRef(null)

  const [rows, setRows]       = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState(pos.filter?.search || '')
  const [filter, setFilter]   = useState(pos.filter?.status || 'all')
  const [selectedId, setSelectedId] = useState(pos.selectedRow || null)
  const [modal, setModal]     = useState(null)  // null | {record} | {record:null}

  // ── načítanie dát ────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetchAuth(CFG.apiBase)
      const d = await r.json()
      setRows(Array.isArray(d) ? d : (d.rows || []))
    } catch (_) {
      toast.error('Dáta sa nenačítali.')
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])
  useLiveRefresh(load)  // živé obnovenie (SSE, ústava 97)

  // ── filter + fulltext hľadanie (ústava sek. 56) ──────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(row => {
      if (filter === 'active'   && !row.active) return false
      if (filter === 'inactive' &&  row.active) return false
      if (!q) return true
      // hľadá vo všetkých textových poliach záznamu
      return Object.values(row).some(v =>
        v != null && hladaj(String(v), q))
    })
  }, [rows, search, filter])

  const selected = useMemo(
    () => rows.find(r => r.id === selectedId) || null,
    [rows, selectedId])

  // ── pamäť pozície — zápis zmien (ústava 53.3) ────────────────────
  useEffect(() => { pos.selectRow(selectedId) }, [selectedId, pos.selectRow])
  useEffect(() => { pos.setFilter({ status: filter, search }) }, [filter, search, pos.setFilter])
  useEffect(() => {
    if (!loading && listRef.current) pos.restoreScroll(listRef.current)
  }, [loading])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── klávesová navigácia zoznamu (ústava 54.2, 56.5) — zdieľaný hook ──
  const openRecord = useCallback((row) => setModal({ record: row }), [])
  useListKeyboard({
    items: filtered,
    selectedId,
    onSelect: setSelectedId,
    onOpen: openRecord,
    enabled: !modal,
  })

  // ── klávesové skratky (ústava sek. 54) ───────────────────────────
  useShortcuts([
    { keys: 'alt+n', label: CFG.newLabel, scope: CFG.resource,
      handler: () => setModal({ record: null }) },
    { keys: '/', label: 'Hľadať', scope: CFG.resource, handler: () => {
      const el = document.querySelector(`[data-${CFG.resource}-search]`)
      if (el) el.focus()
    } },
  ])

  // ── KPI súhrn (ústava sek. 56.8) ─────────────────────────────────
  const kpi = useMemo(() => ({
    total:  rows.length,
    active: rows.filter(r => r.active).length,
  }), [rows])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader title={CFG.titlePlural} subtitle={`${filtered.length} z ${rows.length}`}>
        <ExportMenu
          data={filtered}
          filename={`partie-${todayLocal()}`}
          title="Partie"
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'name', label: 'Názov' },
            { key: 'color', label: 'Farba' },
            { key: 'notes', label: 'Poznámky' },
          ]}
        />
        <BtnAdd onClick={() => setModal({ record: null })}>+ {CFG.newLabel}</BtnAdd>
      </PageHeader>

      <KpiRow cols={2}>
        <KpiCard title="Spolu" color={COLORS.nav} value={kpi.total} />
        <KpiCard title="Aktívne" color={COLORS.add} value={kpi.active} />
      </KpiRow>

      <div style={{ height: SPACE.md }} />

      <MasterDetail
        left={
          <>
            <SearchInput value={search} onChange={setSearch}
              dataAttr={`data-${CFG.resource}-search`}
              placeholder="Hľadať (stlač /)…" />
            <FilterChips options={STATUS_FILTERS} value={filter} onChange={setFilter} />
            <div ref={listRef}
              onScroll={e => pos.saveScroll(e.currentTarget.scrollLeft, e.currentTarget.scrollTop)}
              style={{ flex: 1, overflow: 'auto', padding: 6 }}>
              {loading ? (
                <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
                  Načítavam…
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
                  Žiadne záznamy.
                </div>
              ) : filtered.map(row => (
                <ListRow key={row.id}
                  dataId={row.id}
                  selected={row.id === selectedId}
                  stripColor={row.color || skupinaColor(skupinaFromPartieName(row.name), '', COLORS.line)}
                  onClick={() => setSelectedId(row.id)}
                  onDoubleClick={() => setModal({ record: row })}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text,
                        whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {row.name || '(bez názvu)'}
                      </div>
                      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, marginTop: 2 }}>
                        {row.id}{row.lead_name ? ' · vedúci ' + row.lead_name : ''}{row.member_count ? ' · ' + row.member_count + ' členov' : ''}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <StatusTag status={row.status} />
                      <RowMenu actions={[
                        { key: 'edit',  label: 'Upraviť',         onClick: () => setModal({ record: row }) },
                        { key: 'mem',   label: 'Zoznam členov',   onClick: () => alert('Otvor kartu partie') },
                        { key: 'print', label: 'Tlačiť rozpis',   onClick: () => alert('Tlač — pripravujeme') },
                        { key: 'pdf',   label: 'Uložiť ako PDF',  onClick: () => alert('PDF — pripravujeme') },
                        { separator: true },
                        { key: 'del',   label: 'Archivovať', danger: true, onClick: async () => {
                          const ok = await confirm({
                            title: 'Archivovať partiu?',
                            message: `Naozaj archivovať partiu ${row.id}${row.name ? ' (' + row.name + ')' : ''}?`,
                            confirmLabel: 'Archivovať',
                          })
                          if (!ok) return
                          try {
                            const r = await fetchAuth(`/api/parties/${row.id}`, { method: 'DELETE' })
                            if (!r.ok) throw new Error('DELETE zlyhal')
                            toast.success('Archivované')
                            load()
                          } catch (e) { toast.error('Chyba: ' + e.message) }
                        } },
                      ]} />
                    </div>
                  </div>
                </ListRow>
              ))}
            </div>
            <ListStatusBar>
              ↑↓ výber · Enter detail · Alt+N nový · / hľadať
            </ListStatusBar>
          </>
        }
        right={
          selected
            ? <DetailPane record={selected} onEdit={() => setModal({ record: selected })} />
            : <EmptyPane text="Vyber záznam zo zoznamu vľavo" />
        }
      />

      {modal && (
        <RecordModal
          open
          record={modal.record}
          user={user}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            load()
            toast.success('Uložené.')
          }}
        />
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// STATUS TAG — farebný štítok stavu (ústava 21.15 — z TINT, nie inline)
// ══════════════════════════════════════════════════════════════════
function StatusTag({ status }) {
  const isActive = status === 'active'
  return (
    <span style={{
      fontSize: TYPO.sizes.xs,
      fontWeight: TYPO.weights.semibold,
      color: isActive ? COLORS.add : COLORS.muted,
      background: isActive ? TINT.add : COLORS.lineSoft,
      borderRadius: RADIUS.sm,
      padding: '1px 7px',
    }}>{STATUS_LABEL[status] || status || '—'}</span>
  )
}

// ══════════════════════════════════════════════════════════════════
// DETAIL PANE — pravá strana, taby Detail / História (ústava sek. 56)
// ══════════════════════════════════════════════════════════════════
function DetailPane({ record, onEdit }) {
  const [tab, setTab] = useState('detail')
  return (
    <div style={{
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      height: '100%',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* HLAVIČKA KARTY — tenký stavový pásik + čierny názov (ústava 61.6) */}
      <div style={{ height: 4, background: COLORS.brand }} />
      <div style={{
        display: 'flex', alignItems: 'center', gap: SPACE.md,
        padding: `${SPACE.md}px ${SPACE.lg}px`,
        borderBottom: `1px solid ${COLORS.line}`,
      }}>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra,
          color: COLORS.text }}>
          {record.name || '(bez názvu)'}
        </h2>
        <StatusTag status={record.status} />
        <div style={{ flex: 1 }} />
        <Btn variant="nav" onClick={onEdit}>Upraviť</Btn>
      </div>

      <div style={{ padding: SPACE.md, overflow: 'auto', flex: 1, background: COLORS.bg,
        border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { key: 'detail',    label: 'Detail' },
            { key: 'members',   label: 'Zamestnanci v partii' },
            { key: 'history',   label: 'História' },
          ]}
        />
        {tab === 'detail' ? (
          <div style={{ fontSize: TYPO.sizes.base, lineHeight: 1.7 }}>
            {/* TODO: doplň polia detailu záznamu */}
            <DetailRow label="Názov"    value={record.name} />
            <DetailRow label="Farba"    value={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 18, height: 18, borderRadius: 4, background: record.color || '#999', border: `1px solid ${COLORS.line}` }} />
                <span style={{ fontFamily: TYPO.mono, fontSize: TYPO.sizes.xs }}>{record.color || '—'}</span>
              </span>
            } />
            <DetailRow label="Stav"     value={record.active ? 'Aktívna' : 'Neaktívna'} />
            <DetailRow label="Vytvorená" value={record.created_at} />
            <DetailRow label="Poznámka" value={record.notes} />
            <DetailRow label="Odberateľ v MKsofte" value={<OdberatelMksoft record={record} />} />
          </div>
        ) : tab === 'members' ? (
          <MembersTab partyId={record.id} />
        ) : (
          // audit kto/čo/kedy (ústava 12.0 bod 1).
          // POZN: auto-audit zapisuje entity_id len pri PUT/PATCH/DELETE
          // (úpravy). CREATE má entity_id prázdny — kto vytvoril záznam
          // pribudne keď backend dostane logAudit() helper (ROADMAP, 9.D.6).
          <HistoryPanel module={CFG.resource} entityId={record.id} />
        )}
      </div>
    </div>
  )
}

// ── MKSOFT_VAZBY_V1 (21. 10. 2026, Mirko: „MK soft parovanie nech je v nervovej sústave … napr. partie“): odberateľ náradia v MKsofte
// cez spoločný komponent párovania (štítok, návrh, súhrn, audit) — nahrádza výber z partia_odberatel_v1 (0.J, jeden komponent)
function OdberatelMksoft({ record }) {
  const { user } = useAuth()
  return <StavVazby druh="partia" id={record.id} nazov={record.name} smie={['admin', 'owner', 'office_manager'].includes(user?.role)} />
}

// ── MembersTab — zamestnanci v partii (ústava 3 vlákno) ──────────────────
function MembersTab({ partyId }) {
  const [members, setMembers] = useState(null)
  useEffect(() => {
    fetchAuth(`/api/parties/${partyId}/employees`)
      .then(r => r.ok ? r.json() : [])
      .then(setMembers)
      .catch(() => {})
  }, [partyId])
  const mkStav = useStavVazieb('zamestnanec', (members || []).filter(m => m.active).map(m => m.id))   // MKSOFT_VAZBY_V1: väzba člena na osobu v MKsofte
  if (!members) return <div style={{ padding: SPACE.md, color: COLORS.muted }}>Načítavam…</div>
  const active = members.filter(m => m.active)
  const inactive = members.filter(m => !m.active)
  const partak = active.find(m => m.is_partak)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md, padding: SPACE.sm }}>
      <div style={{ padding: SPACE.md, background: 'rgba(59,130,246,0.06)',
        border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
        fontSize: TYPO.sizes.sm, color: COLORS.muted, fontStyle: 'italic' }}>
        Vlákno z karty zamestnanca → pole „Partia". 1 zamestnanec môže byť partia (ústava 3).
        Parťák píše denník na mobile (ústava 4).
      </div>
      {partak && (
        <div style={{ padding: SPACE.md, background: 'rgba(245,158,11,0.08)',
          border: `1px solid #f59e0b`, borderRadius: RADIUS.md }}>
          <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
            color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Parťák partie
          </div>
          <div style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.bold, marginTop: 2 }}>
            {partak.name || `${partak.firstName || ''} ${partak.lastName || ''}`.trim()}
          </div>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>
            {partak.role || '—'} · {partak.category || '—'} · {partak.phone || ''}
          </div>
          {partak.phone && <div style={{ marginTop: 8 }}>
            <SmsButton to={partak.phone} label="SMS" style={{ padding: '4px 12px', fontSize: 12, fontWeight: 700, border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', borderRadius: 8 }} />
          </div>}
        </div>
      )}
      {!partak && active.length > 0 && (
        <div style={{ padding: SPACE.sm, background: 'rgba(239,68,68,0.06)',
          border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm,
          fontSize: TYPO.sizes.xs, color: COLORS.remove }}>
          Partia nemá parťáka. V karte niektorého zamestnanca zaškrtni PARTAK.
        </div>
      )}
      <div>
        <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
          color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em',
          marginBottom: SPACE.sm }}>Aktívni členovia ({active.length})</div>
        {active.length === 0 ? (
          <div style={{ padding: SPACE.md, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
            Žiadny aktívny zamestnanec. Priraď partiu v karte zamestnanca.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.line}` }}>
                <th style={tdh}>Meno</th>
                <th style={tdh}>Rola</th>
                <th style={tdh}>Skupina</th>
                <th style={tdh}>Národnosť</th>
                <th style={{ ...tdh, textAlign: 'right' }}>Sadzba</th>
              </tr>
            </thead>
            <tbody>
              {active.map(m => (
                <tr key={m.id} style={{ borderBottom: `1px solid ${COLORS.lineSoft}` }}>
                  <td style={tdc}>
                    {m.is_partak && <span title="Parťák" style={{ color: '#f59e0b', marginRight: 4 }}>★</span>}
                    {m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim()}
                    {mkStav[m.id] && <span style={{ marginLeft: 6 }}><StitokVazby s={mkStav[m.id]} /></span>}
                  </td>
                  <td style={tdc}>{m.role || '—'}</td>
                  <td style={tdc}>{m.category || '—'}</td>
                  <td style={tdc}>{m.nationality || 'SK'}</td>
                  <td style={{ ...tdc, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: TYPO.weights.semibold, color: COLORS.total }}>
                    {Number(m.rate || 0).toFixed(2)} {m.payType === 'mesačná' ? '€/mes' : m.payType === 'úkolová' ? '€/m²' : '€/hod'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {inactive.length > 0 && (
        <div>
          <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
            color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: SPACE.sm, marginTop: SPACE.md }}>
            Ukončení ({inactive.length}) — história (ústava 1 soft archív)
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm, opacity: 0.6 }}>
            <tbody>
              {inactive.map(m => (
                <tr key={m.id} style={{ borderBottom: `1px solid ${COLORS.lineSoft}` }}>
                  <td style={tdc}>{m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim()}</td>
                  <td style={tdc}>{m.role || '—'}</td>
                  <td style={{ ...tdc, fontSize: TYPO.sizes.xs, color: COLORS.remove }}>Ukončený</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
const tdh = { padding: '6px 8px', textAlign: 'left', color: COLORS.muted, fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.xs, textTransform: 'uppercase', letterSpacing: '0.06em' }
const tdc = { padding: '6px 8px' }


// ══════════════════════════════════════════════════════════════════
// RECORD MODAL — edit / new cez useRecordEditor (S1)
// Lock + dirty + Form→Confirm + DIFF — všetko zabudované v hooku.
// ══════════════════════════════════════════════════════════════════
function RecordModal({ open, record, user, onClose, onSaved }) {
  const isNew = !record

  const ed = useRecordEditor({
    resource: CFG.resource,
    id: record?.id || null,
    user,
    initial: {
      prefix: 'P',
      name:  record?.name  || '',
      color: record?.color || '#22c55e',
      notes: record?.notes || '',
    },
    fields: EDIT_FIELDS,
    onSubmit: async (values) => {
      const r = await fetchAuth(
        isNew ? CFG.apiBase : `${CFG.apiBase}/${record.id}`,
        { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(values) }
      )
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        // dedup (ústava sek. 13) — backend vracia 409 pri duplicite
        if (r.status === 409) throw new Error(d.message || 'Taký záznam už existuje.')
        throw new Error(d.error || 'Uloženie zlyhalo.')
      }
    },
  })

  if (!open) return null

  const doClose = () => ed.requestClose(onClose)

  const validate = (v) => {
    if (!v.name || !v.name.trim()) return 'Názov je povinný.'
    return null
  }

  const handleConfirm = async () => {
    const ok = await ed.submit()
    if (ok) onSaved()
  }

  const readOnly = !ed.canEdit

  return (
    <>
      <Modal
        open
        onClose={doClose}
        onDirtyClose={doClose}
        isDirty={ed.isDirty}
        title={isNew ? CFG.newLabel : `Upraviť — ${record.name}`}
        subtitle={ed.phase === 'confirm' ? 'Skontroluj zmeny pred uložením' : ''}
        maxWidth={640}
        footer={
          ed.phase === 'form' ? (
            <>
              <BtnGhost onClick={doClose}>Zrušiť</BtnGhost>
              <BtnAdd onClick={() => ed.goConfirm(validate)} disabled={readOnly}>
                Uložiť →
              </BtnAdd>
            </>
          ) : (
            <>
              <BtnGhost onClick={ed.back}>← Späť na úpravu</BtnGhost>
              <BtnAdd onClick={handleConfirm} disabled={ed.submitting}>
                {ed.submitting ? 'Ukladám…' : '✓ Potvrdiť uloženie'}
              </BtnAdd>
            </>
          )
        }
      >
        {/* LOCK banner — záznam upravuje niekto iný (ústava 53.7) */}
        {readOnly && ed.lockedBy && (
          <div style={{
            background: TINT.remove,
            border: `1px solid ${COLORS.remove}`,
            borderRadius: RADIUS.md,
            padding: SPACE.sm,
            marginBottom: SPACE.md,
            fontSize: TYPO.sizes.sm,
            color: COLORS.remove,
          }}>
            Záznam práve upravuje <strong>{ed.lockedBy}</strong>. Editácia je zamknutá.
          </div>
        )}

        {ed.error && (
          <div style={{
            background: TINT.remove,
            border: `1px solid ${COLORS.remove}`,
            borderRadius: RADIUS.md,
            padding: SPACE.sm,
            marginBottom: SPACE.md,
            fontSize: TYPO.sizes.sm,
            color: COLORS.remove,
          }}>{ed.error}</div>
        )}

        {ed.phase === 'form' ? (
          // ── FORM fáza ──────────────────────────────────────────────
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            {isNew && (
              <Field label="Druh partie — určuje číslo (poterári PP1→PP4…)">
                <select
                  value={ed.values.prefix || 'P'}
                  onChange={e => ed.set('prefix', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.md, background: '#fff' }}
                >
                  <option value="PS">PS — Omietky sadra</option>
                  <option value="PC">PC — Omietky cement</option>
                  <option value="PP">PP — Potery</option>
                  <option value="PM">PM — Maliari</option>
                  <option value="PH">PH — Hodinovkári</option>
                  <option value="PZ">PZ — Zateplenie</option>
                  <option value="P">P — Ostatné</option>
                </select>
              </Field>
            )}
            <Field label="Názov partie *">
              <input type="text" value={ed.values.name} disabled={readOnly}
                placeholder="napr. Cement - Tomáš, P1, Maliari - Voltian…"
                onChange={e => ed.set('name', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Farba (vizuálne v Rozpise prác)">
              <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center' }}>
                <input type="color" value={ed.values.color || '#22c55e'} disabled={readOnly}
                  onChange={e => ed.set('color', e.target.value)}
                  style={{ width: 56, height: 40, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, cursor: 'pointer' }} />
                <input type="text" value={ed.values.color || ''} disabled={readOnly}
                  placeholder="#22c55e" maxLength={7}
                  onChange={e => ed.set('color', e.target.value)} style={{ ...inputStyle, fontFamily: TYPO.mono, width: 120 }} />
                <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                  zelená = sadrové · oranžová = vápenno-cementové · žltá = poteráre…
                </span>
              </div>
            </Field>
            <Field label="Poznámka">
              <textarea value={ed.values.notes} disabled={readOnly}
                onChange={e => ed.set('notes', e.target.value)} rows={3}
                style={{ ...inputStyle, resize: 'vertical' }} />
            </Field>
          </div>
        ) : (
          // ── CONFIRM fáza — DIFF prehľad (ústava 53.8) ───────────────
          <div>
            {isNew ? (
              <p style={{ fontSize: TYPO.sizes.base, color: COLORS.text }}>
                Vytvorí sa nový záznam <strong>{ed.values.name}</strong>.
              </p>
            ) : ed.diff.length === 0 ? (
              <p style={{ color: COLORS.muted }}>Žiadne zmeny.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
                <thead>
                  <tr style={{ background: TINT.nav }}>
                    <th style={thStyle}>Pole</th>
                    <th style={thStyle}>Pôvodné</th>
                    <th style={thStyle}>Nové</th>
                  </tr>
                </thead>
                <tbody>
                  {ed.diff.map(d => (
                    <tr key={d.name}>
                      <td style={tdStyle}>{d.label}</td>
                      <td style={{ ...tdStyle, color: COLORS.muted, textDecoration: 'line-through' }}>
                        {d.from}
                      </td>
                      <td style={{ ...tdStyle, color: COLORS.add, fontWeight: TYPO.weights.semibold }}>
                        {d.to}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Modal>

      {/* DIRTY dialóg — Uložiť / Pokračovať / Zahodiť (ústava 53.1) */}
      <Modal.DirtyConfirm {...ed.dirtyProps} />
    </>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
        color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>{label}</div>
      {children}
    </label>
  )
}

const inputStyle = {
  width: '100%',
  padding: '6px 10px',
  fontSize: TYPO.sizes.base,
  fontFamily: 'inherit',
  border: `1px solid ${COLORS.line}`,
  borderRadius: RADIUS.md,
  outline: 'none',
  boxSizing: 'border-box',
}
const thStyle = {
  textAlign: 'left', padding: '6px 10px',
  fontSize: TYPO.sizes.xs, color: COLORS.nav,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: `1px solid ${COLORS.line}`,
}
const tdStyle = {
  padding: '6px 10px',
  borderBottom: `1px solid ${COLORS.lineSoft}`,
}

// ╔══════════════════════════════════════════════════════════════════╗
// ║  DoD CHECKLIST — skopíruj do HANDOFF, odškrtni pred dokončením    ║
// ║  (ústava 9.D.1 bod 3). Modul NIE JE hotový kým nie sú všetky ✅.  ║
// ╠══════════════════════════════════════════════════════════════════╣
// ║  [ ] CFG vyplnené (resource, apiBase, labels)                    ║
// ║  [ ] EDIT_FIELDS = všetky editovateľné polia (kvôli DIFF)        ║
// ║  [ ] LOCK funguje — test 2 okná naraz (53.7)                     ║
// ║  [ ] DIRTY dialóg pri zatvorení s neuloženými zmenami (53.1)     ║
// ║  [ ] Form → Confirm + DIFF pred uložením (12.0/3, 53.8)         ║
// ║  [ ] HistoryPanel ukazuje audit záznamu (12.0/1)                 ║
// ║  [ ] hľadanie v zozname funguje (56)                             ║
// ║  [ ] žiadny plain <select> — len SearchableSelect (56.11)        ║
// ║  [ ] žiadne inline farby pozadia — len TINT/tint() (21.15)       ║
// ║  [ ] dedup — backend vracia 409 pri duplicite (13)               ║
// ║  [ ] mod+N a / skratky + cheatsheet (54)                         ║
// ║  [ ] starý modul porovnaný — všetky polia/funkcie prenesené      ║
// ║  [ ] ÚSTAVA-REVIEW — druhý prechod kódu bod po bode (9.D.3)      ║
// ╚══════════════════════════════════════════════════════════════════╝
